import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { useDebounce } from 'use-debounce';

import {
    FetchPrepareCheckPrepareAll,
    FetchPrepareCheckPatientPrescriptions,
    FinalCheckPatientPrescription,
    FinalCheckPrepareAll,
    HandleUpdateMultiDateSelection,
    fetchDrugInformationSheet,
    HandleFinalCheckMultiDates,
    PrintLabels,
    handleMuteRX,
    DownloadBulkLabel
} from '../../../services/reducers/pharmacy/accountibility/accountibilityNewRx';

import { fetchAllClinics } from "../../../services/actions/pharmacy/clinic/getClinics";
import { FetchAllDoctorsList } from "../../../services/actions/pharmacy/doctors/doctorsList";

import { useCustomSelectors } from '../../../services/selectors/allSelectors';
import { useCustomDispatch } from '../../../hooks';
import { Loader } from '../../../components/common';

import { useAccountibilitySelector } from '../../../services/selectors/accountabilitySelectors';
import { useCustomPatientSelectors } from '../../../services/selectors/patientSelectors';
import { RecommendationLetterEditor } from '../../../components/pharmacy/form/textEditor';
import { PrintIcons } from '../../../assets/svg/icons';
import { formatDateToYYYYMMDD_CA } from '../../../utils/constants/formInitialStates';

import Select, { components } from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { getBulkPrepEligiblePrescriptions } from './prepCheckBulkEligibility';

const prepCheckSaveQueue = new Map();

const mergePrepCheckRecords = (records = []) => {
    const byRxDin = new Map();

    records.forEach((record) => {
        const key = `${record.rxnumber}_${record.din}`;
        const existing = byRxDin.get(key);
        if (!existing) {
            byRxDin.set(key, {
                ...record,
                multiple_date: [...(record.multiple_date || [])],
            });
            return;
        }

        const datesByKey = new Map((existing.multiple_date || []).map((dateRecord) => [dateRecord.date, dateRecord]));
        (record.multiple_date || []).forEach((dateRecord) => {
            datesByKey.set(dateRecord.date, dateRecord);
        });
        existing.multiple_date = Array.from(datesByKey.values());
    });

    return Array.from(byRxDin.values());
};

const queuePrepCheckSave = ({
    payload,
    dispatch,
    formData,
    pickupRoute,
    setAllPatients,
    currentPage,
    patientId,
}) => {
    if (!payload?.patient_id || !Array.isArray(payload?.records) || payload.records.length === 0) return;

    const queueKey = String(payload.patient_id);
    const existing = prepCheckSaveQueue.get(queueKey) || {
        patient_id: payload.patient_id,
        records: [],
        timer: null,
        dispatch,
        formData,
        pickupRoute,
        setAllPatients,
        currentPage,
        patientId,
    };

    existing.records = mergePrepCheckRecords([...existing.records, ...payload.records]);
    existing.dispatch = dispatch;
    existing.formData = formData;
    existing.pickupRoute = pickupRoute;
    existing.setAllPatients = setAllPatients;
    existing.currentPage = currentPage;
    existing.patientId = patientId;

    if (existing.timer) {
        clearTimeout(existing.timer);
    }

    existing.timer = setTimeout(() => {
        const queued = prepCheckSaveQueue.get(queueKey);
        if (!queued) return;
        prepCheckSaveQueue.delete(queueKey);

        queued.dispatch(HandleUpdateMultiDateSelection({
            payload: {
                patient_id: queued.patient_id,
                records: queued.records,
            },
            formData: queued.formData,
            pickupRoute: queued.pickupRoute,
            setAllPatients: queued.setAllPatients,
            currentPage: queued.currentPage,
            patientId: queued.patientId,
        }));
    }, 50);

    prepCheckSaveQueue.set(queueKey, existing);
};

export function Dropdown({ options,
    isSearchable = true,
    optionClass = '',
    formData,
    setFormData,
    onChange,
    type,
    placeholder,
    defaultType,
    commonClass = "w-44",
    isNumberOfDaysRequired,
    setIsNumberOfDaysRequired,
    setPickupRoute }) {

    const [optionsList, setOptionsList] = useState([])

    useEffect(() => {
        const list = options || [];
        if (type === 'route' || type === 'patientRoutes' || type === 'drug_family') {
            const formattedData = list?.map((value) => ({
                label: value,
                value: value,
            }))
            setOptionsList(formattedData)
        } else {
            const formattedData = list?.map((value) => ({
                label: value?.label,
                value: value?.value,
            }))
            setOptionsList(formattedData)
        }
    }, [options, type])

    useEffect(() => {
        if (type === 'list_type' && optionsList?.length > 0 && !formData[type]) {
            setFormData({ ...formData, [type]: optionsList[0] });
        }


        if (type === 'reversed_drug_type' && optionsList?.length > 0 && !formData[type]) {
            setFormData({ ...formData, [type]: optionsList[0] });
        }

        if (type === 'filter_type' && optionsList?.length > 0 && !formData[type]) {
            if (defaultType === 'prep_missed') {
                setFormData({ ...formData, [type]: optionsList[1] })
            } else if (defaultType === 'final_missed') {
                setFormData({ ...formData, [type]: optionsList[2] })
            } else {
                setFormData({ ...formData, [type]: optionsList[0] })
            }
        }

    }, [optionsList, formData, type])

    // Monitor next_number_of_days value changes to update isNumberOfDaysRequired
    useEffect(() => {
        if (type === 'next_number_of_days' && setIsNumberOfDaysRequired) {
            const hasValue = formData[type] && formData[type].value;
            setIsNumberOfDaysRequired(!hasValue);
        }
    }, [formData[type], type, setIsNumberOfDaysRequired])

    const handleSelect = (selectedOptions) => {
        setFormData({ ...formData, [type]: selectedOptions });
        if (onChange) {
            onChange(selectedOptions);
        }
        // Call the handleChange prop if provided

        if (type === 'route') {
            sessionStorage.setItem('pickupRoute', selectedOptions.label);
        }
        if (type === 'next_number_of_days' && setIsNumberOfDaysRequired) {
            // Update the required state based on whether a value is selected
            setIsNumberOfDaysRequired(!selectedOptions || !selectedOptions.value);
        }

        if (type === 'list_type' && (selectedOptions?.value === 'FENTANYL' || selectedOptions?.value === 'EMERGENCY')) {
            setPickupRoute(selectedOptions.value)
        }

    };

    // const customStyle = React.useMemo(() => ({
    //     control: (base) => ({
    //         ...base,
    //         border: '1px solid ' + (isNumberOfDaysRequired ? 'red' : '#e0e0e0'),
    //         borderRadius: '4px',
    //     }),
    //     menu: (base) => ({
    //         ...base,
    //         width: optionClass,
    //         zIndex: 99
    //     }),
    // }), [isNumberOfDaysRequired, optionClass])


    const customStyle = React.useMemo(() => ({
        control: (base, state) => ({
            ...base,
            border: '1px solid ' + (isNumberOfDaysRequired ? 'red' : '#e0e0e0'),
            borderRadius: '4px',
            boxShadow: state.isFocused ? 'none' : base.boxShadow, // removes blue glow
            outline: 'none', // removes default outline
            '&:hover': {
                borderColor: isNumberOfDaysRequired ? 'red' : '#bdbdbd',
            },
        }),
        menu: (base) => ({
            ...base,
            width: optionClass,
            zIndex: 99,
        }),
    }), [isNumberOfDaysRequired, optionClass]);


    return (
        <div className={` ${commonClass}`}>
            <Select
                options={optionsList}
                value={formData[type]}
                onChange={handleSelect}
                placeholder={placeholder}
                isSearchable={isSearchable}
                styles={customStyle}
                className="basic-multi-select"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
            />
        </div>
    )
}
// Consultation Dropdown Component
export function ConsultationDropdown({ consultationText, rxId, onConsultationChange }) {
    const [selectedValue, setSelectedValue] = useState(null);
    const [customOptions, setCustomOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const debounceTimeout = useRef(null);

    // Store custom options in localStorage to persist them
    const storageKey = 'consultationCustomOptions';

    const defaultOptions = [
        { label: 'Dose Changed', value: 'Dose Changed' },
        // { label: 'Consult provided', value: 'Consult provided' },
        { label: 'Consult offered but refused by patient', value: 'Consult offered but refused by patient' },
        { label: 'In person ', value: 'In person' },
        { label: 'Call made', value: 'Call made' },
        { label: 'Written', value: 'Written' },
        { label: 'Patient Refused counselling', value: 'Patient Refused counselling' },
        { label: 'Consultation provided', value: 'Consultation provided' },
    ]

    // [
    //     { label: '*** Dose Changed ***', value: '*** Dose Changed ***' },
    //     { label: '*** Consult provided ***', value: '*** Consult provided ***' },
    //     { label: '*** Consult offered but refused by patient ***', value: '*** Consult offered but refused by patient ***' }
    // ];



    // Load saved custom options on component mount
    useEffect(() => {
        const savedOptions = localStorage.getItem(storageKey);
        if (savedOptions) {
            setCustomOptions(JSON.parse(savedOptions));
        }
    }, []);

    // Initialize customOptions with consultationText if it's not in defaultOptions
    useEffect(() => {
        if (consultationText && !defaultOptions.some(opt => opt.value === consultationText)) {
            setCustomOptions(prev => {
                if (!prev.some(opt => opt.value === consultationText)) {
                    const newOptions = [...prev, { label: consultationText, value: consultationText }];
                    localStorage.setItem(storageKey, JSON.stringify(newOptions));
                    return newOptions;
                }
                return prev;
            });
        }
    }, [consultationText]);

    // Debounced options fetch function
    const fetchOptions = (inputValue) => {
        return new Promise((resolve) => {
            if (!inputValue || inputValue.length < 1) {
                resolve([...defaultOptions, ...customOptions]); // Show all options when no input
                return;
            }

            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }

            debounceTimeout.current = setTimeout(() => {
                const allOptions = [...defaultOptions, ...customOptions];
                const filteredOptions = allOptions.filter(option =>
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                );

                resolve(filteredOptions);
            }, 300); // Reduced debounce time for better responsiveness
        });
    };

    const handleSelectConsultation = (selectedOption) => {
        setSelectedValue(selectedOption);
        onConsultationChange(rxId, selectedOption?.value || '');
    };

    const handleInputChange = (newValue) => {
        setInputValue(newValue);
    };

    const handleCreateOption = (inputValue) => {
        const newOption = { label: inputValue, value: inputValue };

        // Add to custom options if not already present
        setCustomOptions(prev => {
            const exists = [...defaultOptions, ...prev].some(
                opt => opt.value.toLowerCase() === inputValue.toLowerCase()
            );

            if (!exists) {
                const newOptions = [...prev, newOption];
                // Save to localStorage
                localStorage.setItem(storageKey, JSON.stringify(newOptions));
                return newOptions;
            }
            return prev;
        });

        setSelectedValue(newOption);
        onConsultationChange(rxId, newOption.value);
    };

    const consultationStyle = {
        control: (base) => ({
            ...base,
            minHeight: '25px',
            height: '25px',
            fontSize: '12px',
            padding: '0 8px'
        }),
        menu: (base) => ({
            ...base,
            fontSize: '12px'
        }),
        clearIndicator: (base) => ({
            ...base,
            display: 'none' // Hide the clear button
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#4299e1' :
                (!defaultOptions.some(opt => opt.value === state.data.value) ? '#f0f9ff' : base.backgroundColor),
            '&:hover': {
                backgroundColor: state.isSelected ? '#4299e1' : '#e6f3ff'
            },
            padding: '8px 12px',
            cursor: 'pointer'
        }),
        input: (base) => ({
            ...base,
            margin: '0',
            padding: '0'
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 2px'
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '0 4px'
        }),
        menuList: (base) => ({
            ...base,
            padding: '4px 0'
        })
    };

    return (
        <div className="w-[200px]">
            <AsyncCreatableSelect
                value={consultationText ? { label: consultationText, value: consultationText } : null}
                styles={consultationStyle}
                loadOptions={fetchOptions}
                onChange={handleSelectConsultation}
                onInputChange={handleInputChange}
                placeholder="Start typing to search..."
                isClearable={false}
                isSearchable
                className="basic-multi-select"
                classNamePrefix="react-select"
                cacheOptions
                defaultOptions={[...defaultOptions, ...customOptions]}
                onCreateOption={handleCreateOption}
                formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            />
        </div>
    );
}

// Show continue button if all patients have prescriptions checked or marked as checked
export const shouldShowContinueButton = (allPatients, totalPatients) => {
    // Check if we have all required data
    if (!allPatients || allPatients.length === 0 || !totalPatients) return false;
    // Check if we've loaded all patients
    if (allPatients.length !== totalPatients) return false;

    return allPatients.every(patient => {
        // Case 1: Patient has prescriptions checked (prep_check_count > 0)
        if (patient.prep_check_count > 0) return true;

        // Case 2: Patient has no prescriptions and is marked as checked
        if (patient.override_prep_count === 0 &&
            patient.marked_checked === true) return true;

        return false;
    });
};

// Handle prescription data effect
export const handlePrescriptionDataEffect = (selectedPatient, prescriptionData, setAllPatients, setSelectedPrescriptions, setMasterCheckbox, profileData) => {
    if (selectedPatient && prescriptionData) {
        // If no medicines found
        if (prescriptionData?.length === 0) {
            setAllPatients(prevPatients => {
                return prevPatients?.map(p => {
                    if (p?.patient_id === selectedPatient?.patient_id) {
                        return {
                            ...p,
                            marked_checked: true,
                            override_prep_count: 0
                        };
                    }
                    return p;
                });
            });
        }
        // Initialize selectedPrescriptions with prescriptions that are already checked
        // if (prescriptionData?.length > 0 && profileData?.type !== 'pharmacist') {
        //     const checkedPrescriptions = prescriptionData
        //         ?.filter(prescription => prescription.is_checked)
        //         ?.map(prescription => ({
        //             rxnumber: prescription.rxnumber,
        //             din: prescription.din,
        //             is_checked: true
        //         }));
        //     setSelectedPrescriptions(checkedPrescriptions);
        //     setMasterCheckbox(checkedPrescriptions.length === prescriptionData.length);
        // } else {
        //     setSelectedPrescriptions([])
        //     setMasterCheckbox(false)
        // }
    }
};

// Handle individual checkbox
export const handleIndividualCheckbox = (prescription,
    checked,
    selectedPatient,
    formData,
    pickupRoute,
    dispatch,
    setAllPatients,
    setSelectedPrescriptions,
    setMasterCheckbox,
    prescriptionData,
    currentPage) => {
    // Only make API call when checking (not when unchecking)
    // if (checked) {
    // Create payload for single prescription update
    const payload = {
        patient_id: selectedPatient.patient_id,
        search_date: formData.date,
        checks: [{
            rxnumber: prescription.rxnumber,
            din: prescription.din,
            is_checked: checked,
            dose: prescription.changing_frequency_dose
        }]
    };

    // Make API call to update the prescription status
    dispatch(FetchPrepareCheckPrepareAll({
        fetchType: 'fetchPrepareCheckPrepareAll',
        payload: payload,
        formData,
        pickupRoute,
        setAllPatients,
        currentPage
    }));
    // }

    // Update local state
    setSelectedPrescriptions(prev => {
        const updated = checked ?
            [...prev, {
                rxnumber: prescription.rxnumber,
                din: prescription.din,
                is_checked: true
            }] :
            prev.filter(p => p.rxnumber !== prescription.rxnumber);

        // Update master checkbox if all are selected
        setMasterCheckbox(updated.length === prescriptionData?.length);
        return updated;
    });
};



// export const handleConfirmPrepareAll = (
//     prescriptionData,
//     selectedPatient,
//     formData,
//     pickupRoute,
//     dispatch,
//     setAllPatients,
//     setSelectedPrescriptions,
//     setMasterCheckbox,
//     currentPage
// ) => {
//     setMasterCheckbox(true);
//     // Filter only those prescriptions with days_difference === 0
//     const checks = prescriptionData
//         ?.filter(prescription => prescription.days_difference === 0)
//         .map(prescription => ({
//             rxnumber: prescription.rxnumber,
//             din: prescription.din,
//             is_checked: true
//         })) || [];

//     const payload = {
//         patient_id: selectedPatient.patient_id,
//         search_date: formData.date,
//         checks: checks
//     };

//     if (checks?.length > 0) {
//         dispatch(FetchPrepareCheckPrepareAll({
//             payload: payload,
//             formData,
//             pickupRoute,
//             setAllPatients,
//             currentPage
//         }));
//         setSelectedPrescriptions(checks);
//     }
// };

// export const handleMasterCheckbox = (masterCheckbox,
//     setMasterCheckbox,
//     setSelectedPrescriptions,
//     setShowModal,
//     prescriptionData,
//     selectedPatient,
//     formData,
//     pickupRoute,
//     dispatch,
//     setAllPatients,
//     currentPage
// ) => {
//     // If already checked, uncheck all directly without API call
//     if (masterCheckbox) {
//         setMasterCheckbox(false);
//         setSelectedPrescriptions([]);
//         // Skip API call when unchecking all
//     } else {
//         // If unchecked, show confirmation modal before checking all
//         // setShowModal(prev => ({ ...prev, prepareAll: true }));
//         if (prescriptionData?.length > 0) {
//             handleConfirmPrepareAll(prescriptionData,
//                 selectedPatient,
//                 formData,
//                 pickupRoute,
//                 dispatch,
//                 setAllPatients,
//                 setSelectedPrescriptions,
//                 setMasterCheckbox,
//                 currentPage
//             )
//         }
//     }
// };

// Handle patient click


export const handleConfirmPrepareAll = (
    prescriptionData,
    selectedPatient,
    formData,
    pickupRoute,
    dispatch,
    setAllPatients,
    setSelectedPrescriptions,
    isChecked, // true for check all, false for uncheck all
    currentPage
) => {
    // Filter prescriptions with days_difference === 0
    const checks = prescriptionData
        ?.filter(prescription => prescription.days_difference === 0)
        .map(prescription => ({
            rxnumber: prescription.rxnumber,
            din: prescription.din,
            is_checked: isChecked,
            dose: prescription.changing_frequency_dose
        })) || [];

    const payload = {
        patient_id: selectedPatient.patient_id,
        search_date: formData.date,
        checks: checks
    };

    if (checks?.length > 0) {
        dispatch(FetchPrepareCheckPrepareAll({
            payload,
            formData,
            pickupRoute,
            setAllPatients,
            currentPage
        }));

        // Only update local UI selection if checking
        setSelectedPrescriptions(isChecked ? checks : []);
    }
};

export const handleMasterCheckbox = (
    masterCheckbox,
    setMasterCheckbox,
    setSelectedPrescriptions,
    setShowModal,
    prescriptionData,
    selectedPatient,
    formData,
    pickupRoute,
    dispatch,
    setAllPatients,
    currentPage
) => {
    // Toggle master checkbox state
    const newCheckedState = !masterCheckbox;
    setMasterCheckbox(newCheckedState);

    // When no prescription data, skip
    if (!prescriptionData?.length) return;

    // Call dispatch with new checked state (true for check all, false for uncheck all)
    handleConfirmPrepareAll(
        prescriptionData,
        selectedPatient,
        formData,
        pickupRoute,
        dispatch,
        setAllPatients,
        setSelectedPrescriptions,
        newCheckedState, // Pass checked state
        currentPage
    );
};

export const handlePatientClick = (patient,
    formData,
    dispatch,
    setSelectedPatient,
    setMasterCheckbox,
    setMultiDateSelection,
    sort_by
) => {

    setSelectedPatient(patient)
    setMultiDateSelection([])
    setMasterCheckbox(false);

    dispatch(FetchPrepareCheckPatientPrescriptions({
        patient_id: patient.patient_id,
        search_date: formData.date,
        fetchType: 'patientPrescription',
        next_number_of_days: formData?.next_number_of_days?.value,
        medication_status: formData?.list_type?.value,
        sort_by: sort_by || formData?.sort_by?.value
    }))
}



export const handleFinalCheckMasterCheckbox = (
    setMasterCheckbox,
    setSelectedPrescriptions,
    prescriptionData,
    selectedPatient,
    formData,
    dispatch,
) => {
    setMasterCheckbox(true);

    if (prescriptionData?.length > 0) {
        const allPrescriptionIds = prescriptionData.map(prescription => ({
            final_id: prescription.final_chk_record_id,
            is_checked: true
        }));

        setSelectedPrescriptions(allPrescriptionIds);
        const payload = {
            patient_id: selectedPatient.id,
            search_date: formData.date,
            final_id: allPrescriptionIds.map(p => p.final_id),
        };
        dispatch(FinalCheckPrepareAll({
            payload: payload,
            formData,
        }));
    }
};

export const handleFinalCheckIndividualCheckbox = (prescription,
    checked,
    selectedPatient,
    formData,
    dispatch,
    setAllPatients,
    setSelectedPrescriptions,
    setMasterCheckbox,
    prescriptionData) => {
    // Only make API call when checking (not when unchecking)
    if (checked) {
        // Create payload for single prescription update
        const payload = {
            patient_id: selectedPatient.id,
            search_date: formData.date,
            final_id: prescription.final_chk_record_id,
        };

        // Make API call to update the prescription status
        dispatch(FinalCheckPrepareAll({
            payload: payload,
            formData,
        }));
    }

    // Update local state
    setSelectedPrescriptions(prev => {
        const updated = checked ?
            [...prev, {
                final_id: prescription.final_chk_record_id,
                is_checked: true
            }] :
            prev.filter(p => p.final_id !== prescription.final_chk_record_id);

        // Update master checkbox if all are selected
        setMasterCheckbox(updated.length === prescriptionData?.length);
        return updated;
    });
};

export const handleFinalCheckPatientClick = (patient,
    formData,
    dispatch,
    setSelectedPatient,
    setSelectedPrescriptions,
    setMasterCheckbox) => {
    setSelectedPatient(patient)
    setSelectedPrescriptions([]);
    setMasterCheckbox(false);
    dispatch(FinalCheckPatientPrescription({
        patientId: patient.id,
        searchDate: formData.date,
        fetchType: 'finalCheckPatientPrescription'
    }))
}


// 🧠 This function is used to handle the multi date checkbox in preparing check
// export const handleMultiDateCheckbox = ({
//     patient_id,
//     rxnumber,
//     din,
//     date,
//     checked,
//     setMultiDateSelection,
//     dispatch,
//     formData,
//     pickupRoute,
//     setAllPatients,
//     currentPage,
//     totalDates,
//     Index,
//     rxstop_date
// }) => {
//     let finalUpdatedData = [];

//     const rxStopDate = rxstop_date ? new window.Date(rxstop_date) : null;

//     setMultiDateSelection(prev => {
//         let updated = [...prev];

//         // Find if entry exists for this RX
//         let existing = updated.find(item =>
//             item.patient_id === patient_id &&
//             item.rxnumber === rxnumber &&
//             item.din === din
//         );

//         if (!existing) {
//             // Create a full structure with all dates
//             const multiple_date = totalDates.map((d, idx) => {
//                 const isEmergency = rxStopDate ? new window.Date(d.formated_date) > rxStopDate : false;
//                 const scheduledEmergencyDay = d.delivery_status === 'Emergency' ? true : false;
//                 const hasEmergency = (isEmergency || scheduledEmergencyDay);
//                 const record = {
//                     is_checked: idx === Index && checked,
//                     date: d.formated_date,
//                     dose: d.dose,
//                     refills_left: d.refills_left,
//                     qty_left: d.qty_left,
//                     is_emergency: hasEmergency
//                 };
//                 return record;
//             });

//             updated.push({
//                 patient_id,
//                 rxnumber,
//                 din,
//                 multiple_date
//             });
//         } else {
//             // Update is_checked for the correct date
//             existing.multiple_date = totalDates.map((d, idx) => {
//                 const isCurrent = d.formated_date === date;
//                 const isEmergency = rxStopDate ? new window.Date(d.formated_date) > rxStopDate : false;
//                 const scheduledEmergencyDay = d.delivery_status === 'Emergency' ? true : false;
//                 const hasEmergency = (isEmergency || scheduledEmergencyDay);
//                 const record = {
//                     is_checked: isCurrent ? checked :
//                         existing.multiple_date?.find(md => md.date === d.formated_date)?.is_checked || false,
//                     date: d.formated_date,
//                     dose: d.dose,
//                     refills_left: d.refills_left,
//                     qty_left: d.qty_left,
//                     is_emergency: hasEmergency
//                 };
//                 return record;
//             });
//         }

//         finalUpdatedData = [...updated];
//         return finalUpdatedData;
//     });

//     // 🔁 Defer API payload trigger after state update
//     setTimeout(() => {
//         if (finalUpdatedData.length > 0) {
//             isMultiDateSelectionApiPayloadData(
//                 finalUpdatedData,
//                 patient_id,
//                 dispatch,
//                 formData,
//                 pickupRoute,
//                 setAllPatients,
//                 currentPage
//             );
//         }
//     }, 0);
// };


// 🧠 Optimized function to handle multi-date checkbox logic in preparation check
// export const handleMultiDateCheckbox = ({
//     patient_id,
//     rxnumber,
//     din,
//     date,
//     checked,
//     setMultiDateSelection,
//     dispatch,
//     formData,
//     pickupRoute,
//     setAllPatients,
//     currentPage,
//     totalDates,
//     Index,
//     rxstop_date,
//     emegergencySig
// }) => {
//     let finalUpdatedData = [];

//     const rxStopDate = rxstop_date ? new window.Date(rxstop_date) : null;

//     const buildDateRecord = (d, idx, existingCheckedDates = []) => {
//         const isCurrent = d.formated_date === date;
//         const isEmergency = rxStopDate ? new window.Date(d.formated_date) > rxStopDate : false;
//         const scheduledEmergencyDay = d.delivery_status === 'Emergency';
//         const hasEmergency = (isEmergency || scheduledEmergencyDay || emegergencySig);
//         return {
//             is_checked: isCurrent ? checked : existingCheckedDates.find(md => md.date === d.formated_date)?.is_checked || false,
//             date: d.formated_date,
//             dose: d.dose,
//             refills_left: d.refills_left,
//             qty_left: d.qty_left,
//             is_emergency: hasEmergency
//         };
//     };

//     setMultiDateSelection(prev => {
//         let updated = [...prev];

//         let existing = updated.find(item =>
//             item.patient_id === patient_id &&
//             item.rxnumber === rxnumber &&
//             item.din === din
//         );

//         if (!existing) {
//             const multiple_date = totalDates.map((d, idx) => buildDateRecord(d, idx));
//             // Ensure only clicked date is checked in initial insert
//             multiple_date[Index].is_checked = checked;

//             updated.push({
//                 patient_id,
//                 rxnumber,
//                 din,
//                 multiple_date
//             });
//         } else {
//             existing.multiple_date = totalDates.map((d, idx) =>
//                 buildDateRecord(d, idx, existing.multiple_date)
//             );
//         }

//         finalUpdatedData = [...updated];
//         return finalUpdatedData;
//     });

//     // 🔁 Trigger API after state is updated
//     setTimeout(() => {
//         if (finalUpdatedData.length > 0) {
//             isMultiDateSelectionApiPayloadData(
//                 finalUpdatedData,
//                 patient_id,
//                 dispatch,
//                 formData,
//                 pickupRoute,
//                 setAllPatients,
//                 currentPage
//             );
//         }
//     }, 0);
// };



export const handleMultiDateCheckbox = ({
    patient_id,
    rxnumber,
    din,
    date,
    checked,
    setMultiDateSelection,
    dispatch,
    formData,
    pickupRoute,
    setAllPatients,
    currentPage,
    totalDates,
    // Index,
    rxstop_date,
    emegergencySig,
    lotExpiryByRx = {},
    onPrepCheckConfirm,
    prepCheckModalItems = [],
    patientId
}) => {
    // When user checks a dose: show prep-check confirmation modal with medication/lot/expiry details
    if (checked && typeof onPrepCheckConfirm === 'function' && prepCheckModalItems?.length > 0) {
        onPrepCheckConfirm(prepCheckModalItems);
    }

    const rxStopDate = rxstop_date ? new window.Date(rxstop_date) : null;

    // ⏱️ Get info for only the triggered date
    const selectedDateData = totalDates.find(d => d.formated_date === date);

    if (!selectedDateData) return;

    const isEmergency = rxStopDate ? new window.Date(date) > rxStopDate : false;
    const scheduledEmergencyDay = selectedDateData.delivery_status === 'Emergency' ? true : false;
    const hasEmergency = (isEmergency || scheduledEmergencyDay || emegergencySig);

    // ⬇️ Construct single record payload
    const payload = [
        {
            patient_id,
            rxnumber,
            din,
            multiple_date: [
                {
                    is_checked: checked,
                    date: date,
                    dose: selectedDateData.dose,
                    refills_left: selectedDateData.refills_left,
                    qty_left: selectedDateData.qty_left,
                    is_emergency: hasEmergency
                }
            ]
        }
    ];

    // ⏫ Update local state too (if needed)
    setMultiDateSelection(prev => {
        const updated = [...prev];
        const existing = updated.find(
            item =>
                item.patient_id === patient_id &&
                item.rxnumber === rxnumber &&
                item.din === din
        );

        if (!existing) {
            updated.push({
                patient_id,
                rxnumber,
                din,
                multiple_date: [
                    {
                        is_checked: checked,
                        date,
                        dose: selectedDateData.dose,
                        refills_left: selectedDateData.refills_left,
                        qty_left: selectedDateData.qty_left,
                        is_emergency: hasEmergency
                    }
                ]
            });
        } else {
            const dateEntry = existing.multiple_date.find(
                d => d.date === date
            );
            if (dateEntry) {
                dateEntry.is_checked = checked;
            } else {
                existing.multiple_date.push({
                    is_checked: checked,
                    date,
                    dose: selectedDateData.dose,
                    refills_left: selectedDateData.refills_left,
                    qty_left: selectedDateData.qty_left,
                    is_emergency: hasEmergency
                });
            }
        }

        return updated;
    });

    // 🚀 Only send single triggered date's info
    setTimeout(() => {
        isMultiDateSelectionApiPayloadData(
            payload,
            patient_id,
            dispatch,
            formData,
            pickupRoute,
            setAllPatients,
            currentPage,
            lotExpiryByRx,
            patientId
        );
    }, 0);
};


// 🧠 This function is used to handle the master checkbox toggle  prepare check
export const handleMasterCheckboxToggle = ({ checked,
    setMasterCheckbox,
    prescriptionData,
    setMultiDateSelection,
    dispatch,
    formData,
    pickupRoute,
    setAllPatients,
    currentPage,
    lotExpiryByRx = {},
    onPrepCheckConfirm,
    patientId
}) => {
    if (!prescriptionData || prescriptionData?.length === 0) return;

    const eligiblePrescriptions = getBulkPrepEligiblePrescriptions(
        prescriptionData,
        formData?.date
    );

    // Use the same eligible set for confirmation and persistence so the UI
    // never confirms a medication that the save payload silently omits.
    if (checked && typeof onPrepCheckConfirm === 'function' && eligiblePrescriptions.length > 0) {
        const items = eligiblePrescriptions.map(prescription => {
            const key = `${prescription.rxnumber}_${prescription.patient_id}`;
            const le = lotExpiryByRx[key] || {};
            const lot = le.lot != null ? String(le.lot).trim() : '';
            const expiry = le.expiry != null ? String(le.expiry).trim() : '';
            return {
                medicationName: prescription.drug || `RX ${prescription.rxnumber}`,
                lot,
                expiry
            };
        });
        onPrepCheckConfirm(items);
    }

    // Set master checkbox state
    setMasterCheckbox(checked);

    const patient_id = prescriptionData[0]?.patient_id;
    // Create data for all prescriptions
    const allData = eligiblePrescriptions.map(prescription => {
        const rxStopDate = prescription.rxstop_date ? new window.Date(prescription.rxstop_date) : null;
        let emegergencySig = prescription?.sig && prescription?.sig.toLowerCase().includes('emergency') ? true : false;

        return {
            patient_id: prescription.patient_id,
            rxnumber: prescription.rxnumber,
            din: prescription.din,
            multiple_date: (prescription.dates || []).map(value => {
                const isEmergency = rxStopDate ? new window.Date(value.formated_date) > rxStopDate : false;
                const scheduledEmergencyDay = value.delivery_status === 'Emergency' ? true : false;
                const hasEmergency = (isEmergency || scheduledEmergencyDay || emegergencySig);
                const record = {
                    is_checked: checked,
                    date: value.formated_date,
                    dose: value.dose,
                    refills_left: value.refills_left,
                    qty_left: value.qty_left,
                    is_emergency: hasEmergency
                }
                return record;
            })
        };
    });

    // Update multi-date selection state
    setMultiDateSelection(checked ? allData : []);

    // Make API call to update the backend
    if (allData && allData.length > 0) {
        isMultiDateSelectionApiPayloadData(
            allData,
            patient_id,
            dispatch,
            formData,
            pickupRoute,
            setAllPatients,
            currentPage,
            lotExpiryByRx,
            patientId
        )
    }
};


// 🧠 This function is used to convert the multiDateSelection state to the API payload format
export const isMultiDateSelectionApiPayloadData = (multiDateSelection,
    patient_id,
    dispatch,
    formData,
    pickupRoute,
    setAllPatients,
    currentPage,
    lotExpiryByRx = {},
    patientId) => {

    const records = multiDateSelection.map(({ rxnumber, din, multiple_date }) => {
        const key = `${rxnumber}_${patient_id}`;
        const lotExpiry = lotExpiryByRx[key] || {};
        return {
            rxnumber,
            din,
            multiple_date: multiple_date.map(entry => ({
                is_checked: entry.is_checked,
                date: entry.date,
                dose: entry.dose,
                refills_left: entry.refills_left,
                qty_left: entry.qty_left,
                is_emergency: entry.is_emergency,
                lot_number: lotExpiry.lot ?? '',
                expiry_date: lotExpiry.expiry ?? '',
            }))
        };
    });

    const payload = {
        patient_id,
        records
    };

    queuePrepCheckSave({
        payload,
        dispatch,
        formData,
        pickupRoute,
        setAllPatients,
        currentPage,
        patientId
    });
    return payload;
};

export const handleDrugInformationSheet = ({ din, gender, rxnumber, dispatch, drug }) => {
    const formattedDIN = formatDIN(din)
    const payload = {
        din: formattedDIN,
        gender: gender,
        medname: drug
    }
    dispatch(fetchDrugInformationSheet({
        fetchType: `fetchDrugInformationSheet-${rxnumber}`,
        payload
    }))
}

export const formatDIN = (din) => {
    return din.toString().padStart(8, '0');
};


export const sortRoutes = (routes) => {
    return routes.sort((a, b) => {
        // Keep special accountability routes after normal route names.
        if (a.toLowerCase() === 'flexi') return 1;
        if (b.toLowerCase() === 'flexi') return -1;
        if (a.toLowerCase() === 'open') return 1;
        if (b.toLowerCase() === 'open') return -1;

        const parse = (route) => {
            const match = route.match(/^(\d+)([A-Z]+)$/);
            return match ? [parseInt(match[1]), match[2]] : [0, route];
        };
        const [numA, strA] = parse(a);
        const [numB, strB] = parse(b);

        if (numA !== numB) return numA - numB;
        return strA.localeCompare(strB);
    });
};



// Final check save button
export const handleFinalCheckMasterCheckboxV1 = ({ checked,
    setMasterCheckbox,
    setSelectedPrescriptions,
    prescriptionData,
    dispatch,
    formData,
    setAllPatients,
    currentPage
}) => {

    setMasterCheckbox(checked);
    const patient_id = prescriptionData[0].patient_id
    const allData = prescriptionData
        ?.filter(prescription => prescription.final_chk_record)
        ?.map(prescription => {
            return {
                patient_id: prescription.patient_id,
                rxnumber: prescription.rxnumber,
                multiple_date: (prescription.dates || [])?.map(value => ({
                    is_checked: checked,
                    date: value.formated_date,
                    final_chk_record_id: value?.final_chk_record_id
                }))
            };
        });

    handleFinalCheckMultiDatesApiPayloadData(
        allData,
        patient_id,
        dispatch,
        formData,
        setAllPatients,
        currentPage
    )

    setSelectedPrescriptions(checked ? allData : []);
};

export const handleFinalCheckMultiDateCheckboxV1 = ({
    patient_id,
    rxnumber,
    checked,
    setSelectedPrescriptions,
    totalDates,
    date,
    Index,
    finalCheck_record_id,
    dispatch,
    formData,
    setAllPatients,
    currentPage
}) => {
    let finalUpdatedData = [];

    setSelectedPrescriptions(prev => {
        let updated = [...prev];

        // Find if entry exists for this RX
        let existing = updated.find(item =>
            item.patient_id === patient_id &&
            item.rxnumber === rxnumber
        );

        if (!existing) {
            // Create a full structure with all dates
            const multiple_date = totalDates.map((d, idx) => ({
                is_checked: idx === Index && checked,
                date: d.formated_date,
                final_chk_record_id: d?.final_chk_record_id
            }));
            updated.push({
                patient_id,
                rxnumber,
                multiple_date
            });
        } else {
            // Update is_checked for the correct date
            existing.multiple_date = totalDates.map((d, idx) => {
                const isCurrent = d.formated_date === date;
                return {
                    is_checked: isCurrent ? checked :
                        existing.multiple_date
                            ?.find(md => md.date === d.formated_date)
                            ?.is_checked || false,
                    date: d.formated_date,
                    final_chk_record_id: d?.final_chk_record_id
                };
            });
        }

        finalUpdatedData = [...updated];
        return finalUpdatedData;
    });

    // 🔁 Defer API payload trigger after state update
    setTimeout(() => {
        if (finalUpdatedData.length > 0) {
            handleFinalCheckMultiDatesApiPayloadData(
                finalUpdatedData,
                patient_id,
                dispatch,
                formData,
                setAllPatients,
                currentPage
            );
        }
    }, 0);
};

export const handleFinalCheckMultiDatesApiPayloadData = (multiDateSelection,
    patient_id,
    dispatch,
    formData,
    setAllPatients,
    currentPage
) => {

    const records = multiDateSelection?.map(({ rxnumber, multiple_date }) => ({
        rxnumber,
        multiple_date: multiple_date.map(entry => ({
            is_checked: entry.is_checked,
            date: entry.date,
            final_chk_record_id: entry.final_chk_record_id
        }))
    }));

    const payload = {
        patient_id,
        records
    };

    dispatch(HandleFinalCheckMultiDates({
        payload,
        formData,
        setAllPatients,
        currentPage
    }))
    return payload;
};



// 🧠 This function is used to get the rx number. Use as="span" for inline (e.g. inside a paragraph).
export const RxNumber = ({ rx_number, className_1 = 'text-sm text-gray-600', as: Tag = 'p' }) => {
    return (
        <Tag
            className={`${className_1} cursor-pointer`}
            onClick={() => window.open(`/pharmacy-investigate-rx?rx-number=${rx_number}`, '_blank')}
        >
            {rx_number}
        </Tag>
    );
}

// Add sorting logic for missedRxData
export const sortingMissedRxData = (missedRxData) => {
    return missedRxData?.slice().sort((a, b) => {
        // Get the first date from dates array for both items
        const dateA = a.dates?.[0]?.misseddate1 ? new Date(a.dates[0].misseddate1) : new Date(0);
        const dateB = b.dates?.[0]?.misseddate1 ? new Date(b.dates[0].misseddate1) : new Date(0);
        return dateB - dateA; // Latest date first (decreasing order)
    });
};

// Reusable sorting function for sorting by RX, DIN, Stop Date, or Route.
// sortType: 'rx', 'din', 'stop_date', or 'route'
// sortDirection defaults preserve the existing page behavior.
export const sortBillingData = (data, sortType = 'route', sortDirection) => {
    if (!data || !Array.isArray(data)) return data;

    const resolvedDirection = sortDirection || (sortType === 'rx' || sortType === 'stop_date' ? 'desc' : 'asc');
    const directionFactor = resolvedDirection === 'desc' ? -1 : 1;
    
    return data.slice().sort((a, b) => {
        if (sortType === 'route') {
            // Sort by route first - ensure route is converted to string
            const routeA = String(a.route || '').trim();
            const routeB = String(b.route || '').trim();
            
            // Handle "open" routes - they should appear at the end
            if (routeA.toLowerCase() === 'open') return 1;
            if (routeB.toLowerCase() === 'open') return -1;
            
            // Parse route format - handles "1A", "1AA", "1 AA", "2A", "3B", "W1", etc.
            const parseRoute = (route) => {
                // Ensure route is a string before calling replace
                const routeString = String(route || '');
                // Remove spaces and convert to uppercase for consistent comparison
                const normalizedRoute = routeString.replace(/\s+/g, '').toUpperCase();
                
                // Try to match: number followed by letters (e.g., "1A", "1AA", "2D", "10C")
                const numberLetterMatch = normalizedRoute.match(/^(\d+)([A-Z]+)$/);
                if (numberLetterMatch) {
                    return {
                        type: 'number-letter',
                        number: parseInt(numberLetterMatch[1], 10),
                        letters: numberLetterMatch[2],
                        original: route
                    };
                }
                
                // Try to match: letter(s) followed by number (e.g., "W1", "AB2")
                const letterNumberMatch = normalizedRoute.match(/^([A-Z]+)(\d+)$/);
                if (letterNumberMatch) {
                    return {
                        type: 'letter-number',
                        letters: letterNumberMatch[1],
                        number: parseInt(letterNumberMatch[2], 10),
                        original: route
                    };
                }
                
                // If no match, treat as string
                return {
                    type: 'string',
                    number: 999999, // Put non-matching routes at the end
                    letters: normalizedRoute,
                    original: route
                };
            };
            
            const parsedA = parseRoute(routeA);
            const parsedB = parseRoute(routeB);
            
            // Number-letter routes come first, sorted by number then letters
            if (parsedA.type === 'number-letter' && parsedB.type === 'number-letter') {
                if (parsedA.number !== parsedB.number) {
                    return (parsedA.number - parsedB.number) * directionFactor; // Sort by number: 1, 2, 3, 4...
                }
                // If same number, sort by letters: A, AA, B, C, D...
                return parsedA.letters.localeCompare(parsedB.letters) * directionFactor;
            }
            
            // Number-letter routes come before letter-number routes
            if (parsedA.type === 'number-letter' && parsedB.type !== 'number-letter') return -1 * directionFactor;
            if (parsedA.type !== 'number-letter' && parsedB.type === 'number-letter') return 1 * directionFactor;
            
            // Letter-number routes sorted by letters then number
            if (parsedA.type === 'letter-number' && parsedB.type === 'letter-number') {
                const letterCompare = parsedA.letters.localeCompare(parsedB.letters);
                if (letterCompare !== 0) return letterCompare * directionFactor;
                return (parsedA.number - parsedB.number) * directionFactor;
            }
            
            // Fallback to string comparison
            return parsedA.letters.localeCompare(parsedB.letters) * directionFactor;
            
        } else if (sortType === 'rx') {
            // Sort by RX number.
            // Ensure rxnumber is converted to string (handles null, undefined, numbers, etc.)
            const rxA = String(a.rxnumber || '');
            const rxB = String(b.rxnumber || '');
            
            // Extract numeric value from RX number (handles "01", "51", "23", etc.)
            const getRxNumericValue = (rx) => {
                // Ensure rx is a string before calling replace
                const rxString = String(rx || '');
                // Remove any non-numeric characters and parse as integer
                const numericPart = rxString.replace(/\D/g, '');
                return numericPart ? parseInt(numericPart, 10) : 0;
            };
            
            const numA = getRxNumericValue(rxA);
            const numB = getRxNumericValue(rxB);
            
            if (numA !== numB) {
                return (numA - numB) * directionFactor;
            }
            
            // If numeric values are the same, sort by string (for any non-numeric parts)
            return rxA.localeCompare(rxB) * directionFactor;
        } else if (sortType === 'din') {
            const dinA = String(a.din || '');
            const dinB = String(b.din || '');

            const getDinNumericValue = (din) => {
                const dinString = String(din || '');
                const numericPart = dinString.replace(/\D/g, '');
                return numericPart ? parseInt(numericPart, 10) : Number.MAX_SAFE_INTEGER;
            };

            const numA = getDinNumericValue(dinA);
            const numB = getDinNumericValue(dinB);

            if (numA !== numB) {
                return (numA - numB) * directionFactor;
            }

            return dinA.localeCompare(dinB) * directionFactor;
        } else if (sortType === 'stop_date') {
            const getStopDateKey = (value) => {
                if (!value) return '';

                const rawValue = String(value).trim();
                if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
                    return rawValue;
                }

                const normalizedValue = rawValue.replace(/,/g, '');
                const parts = normalizedValue.split(/\s+/);
                if (parts.length === 3) {
                    const [day, month, year] = parts;
                    const monthIndex = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
                        .indexOf(month.toLowerCase().slice(0, 3));

                    if (monthIndex !== -1) {
                        return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(Number(day)).padStart(2, '0')}`;
                    }
                }

                // Timestamp/date-time values are normalized in APP_TIMEZONE before sorting.
                return formatDateToYYYYMMDD_CA(value);
            };

            const stopDateA = getStopDateKey(a.end_date || a.stop_date || a.stopDate);
            const stopDateB = getStopDateKey(b.end_date || b.stop_date || b.stopDate);

            if (stopDateA !== stopDateB) {
                return stopDateA.localeCompare(stopDateB) * directionFactor;
            }

            return String(a.end_date || a.stop_date || a.stopDate || '')
                .localeCompare(String(b.end_date || b.stop_date || b.stopDate || '')) * directionFactor;
        }
        
        return 0;
    });
};

// 🧠 This function is used to print the labels for the selected dates IN PREPARING CHECK
export const handlePrintLabelsForPreparingCheck = async (rxnumber, patient_id, rxstop_date, emergencySig, labelsSelection, dispatch) => {
    // 🔁 This is used to check if the selected dates are present in the labelsSelection state
    const matchedRx = labelsSelection?.find(item =>
        item.rxnumber === rxnumber &&
        item.multiple_date?.some(d => d.is_checked)
    );

    if (!matchedRx) {
        return;
    }

    // 🔁 This is used to get the selected dates from the matchedRx
    const selectedDates = matchedRx.multiple_date.filter(d => d.is_checked);

    // 🔁 This is used to get the rxstop date from the matchedRx
    const rxStopDate = rxstop_date ? new window.Date(rxstop_date) : null; // Use standard Date constructor

    // 🔁 This is used to count the emergency counter
    let emergencyCounter = 1;
    const records = selectedDates.map(d => {
        const isEmergency = rxStopDate ? new window.Date(d.date) > rxStopDate : false;
        const scheduledEmergencyDay = d.delivery_status === 'Emergency' ? true : false;
        const record = {
            is_emergency: (isEmergency || scheduledEmergencyDay),
            date: d.date,
            qty_left: (isEmergency || emergencySig || scheduledEmergencyDay) ? 0 : d.qty_left,
            dose: d.dose
        };
        if (isEmergency) {
            record.is_emergency_no = `E${emergencyCounter}`;
            emergencyCounter++;
        }
        return record;
    });

    const payload = {
        rxnumber,
        patient_id,
        records
    };
    dispatch(PrintLabels({
        payload,
        fetchType: `printLabels-${rxnumber}-${patient_id}` // Use template literals for clarity
    }));
};

// 🧠 This function is used to check if the pharmacist stamp is present in the preparing check
export const CheckPharmacistStampInPrep = (patient_data) => {
    if (patient_data?.length === 0) {
        return false;
    }
    return patient_data?.some(patient => (patient?.is_prep_check_pharmacist || patient?.is_prep_check_assistant));
}

// 🧠 This is used to get the next number of days
export const NextNumberOfDays = [
    { label: 'Same Day', value: '0' },
    { label: 'Next 1 Day', value: '1' },
    { label: 'Next 2 Days', value: '2' },
    { label: 'Next 3 Days', value: '3' },
    { label: 'Next 4 Days', value: '4' },
    { label: 'Next 5 Days', value: '5' },
    { label: 'Next 6 Days', value: '6' },
    { label: 'Next 7 Days', value: '7' },
    { label: 'Next 14 Days', value: '14' }
]

// 🧠 This is used to get the list type
export const listType = [
    { label: 'All', value: '0' },
    { label: 'Active', value: '1' },
    { label: 'Fentanyl', value: 'FENTANYL' },
    { label: 'Emergency', value: 'EMERGENCY' },
]

export const patientMedicationSortOptions = [
    { label: 'Sort By', value: '' },
    { label: 'DIN', value: 'din' },
    { label: 'Narcotic', value: 'narcotic' },
    { label: 'Non Narcotic', value: 'non-narcotic' },
]

export const narcoticOptions = [
    { label: 'Narcotic', value: 'narkotic' },
    { label: 'Non-Narcotic', value: 'non-narkotic' },
    { label: 'All', value: 'all' },
]

/** Modal shown when user checks any prep or overall prep. Lists medications with lot/expiry. */
export const ExpiryTodayConfirmModal = ({ isOpen, onClose, items = [], onConfirm, patientName }) => {
    const [editableItems, setEditableItems] = useState(items);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const expiryPickerRefs = useRef({});

    const getItemGroupKey = useCallback((item) => {
        const din = item?.din != null ? String(item.din).trim().toLowerCase() : '';
        const medication = item?.medicationName != null ? String(item.medicationName).trim().toLowerCase() : '';
        return `${din || 'no-din'}_${medication || 'medication'}`;
    }, []);

    const handleGroupChange = useCallback((groupIndexes, field, value) => {
        const indexSet = new Set(groupIndexes);
        setEditableItems((previous) => previous.map((item, itemIdx) => {
            if (!indexSet.has(itemIdx)) return item;
            return {
                ...item,
                [field]: value,
                ...(field === 'expiry' ? { formatted_expiry_date: '' } : {})
            };
        }));
    }, []);

    useEffect(() => {
        setEditableItems(items);
        setIsSubmitting(false);
    }, [items]);

    const displayItems = useMemo(() => {
        const grouped = new Map();
        editableItems.forEach((item, idx) => {
            const key = getItemGroupKey(item);
            if (!grouped.has(key)) {
                grouped.set(key, {
                    ...item,
                    groupKey: key,
                    sourceIndexes: [idx],
                    duplicateCount: 1
                });
                return;
            }

            const existing = grouped.get(key);
            existing.sourceIndexes.push(idx);
            existing.duplicateCount += 1;
            if (!existing.lot && item.lot) existing.lot = item.lot;
            if (!existing.expiry && item.expiry) existing.expiry = item.expiry;
            if (!existing.formatted_expiry_date && item.formatted_expiry_date) {
                existing.formatted_expiry_date = item.formatted_expiry_date;
            }
        });
        return Array.from(grouped.values());
    }, [editableItems, getItemGroupKey]);

    useEffect(() => {
        if (!isOpen) {
            Object.values(expiryPickerRefs.current).forEach((instance) => instance?.destroy?.());
            expiryPickerRefs.current = {};
            return;
        }

        const currentKeys = new Set(displayItems.map((_, idx) => String(idx)));
        Object.keys(expiryPickerRefs.current).forEach((key) => {
            if (!currentKeys.has(key)) {
                expiryPickerRefs.current[key]?.destroy?.();
                delete expiryPickerRefs.current[key];
            }
        });

        const timer = setTimeout(() => {
            displayItems.forEach((item, idx) => {
                const key = String(idx);
                const element = document.getElementById(`prep_confirm_expiry_${key}`);
                if (!element) return;

                if (expiryPickerRefs.current[key]?.element && expiryPickerRefs.current[key].element !== element) {
                    expiryPickerRefs.current[key]?.destroy?.();
                    delete expiryPickerRefs.current[key];
                }

                if (!element._flatpickr) {
                    expiryPickerRefs.current[key] = flatpickr(element, {
                        dateFormat: 'Y-m-d',
                        allowInput: false,
                        clickOpens: true,
                        defaultDate: item.expiry || null,
                        onChange: (_selectedDates, dateStr) => {
                            handleGroupChange(item.sourceIndexes, 'expiry', dateStr);
                        }
                    });
                    return;
                }

                if ((item.expiry || '') !== (element.value || '')) {
                    expiryPickerRefs.current[key]?.setDate?.(item.expiry || null, false);
                }
            });
        }, 0);

        return () => clearTimeout(timer);
    }, [isOpen, displayItems, handleGroupChange]);

    useEffect(() => () => {
        Object.values(expiryPickerRefs.current).forEach((instance) => instance?.destroy?.());
        expiryPickerRefs.current = {};
    }, []);

    if (!isOpen) return null;

    const handleConfirm = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            if (typeof onConfirm === 'function') {
                const result = await onConfirm(editableItems);
                if (result === false) return;
            }
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = () => {
        if (isSubmitting) return;
        onClose();
    };

    const formatExpiryFromDateStr = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string') return '';
        const trimmed = dateStr.trim();
        const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) return trimmed;
        const [, y, m, d] = match;
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthIndex = parseInt(m, 10) - 1;
        const day = parseInt(d, 10);
        if (monthIndex >= 0 && monthIndex < 12 && !Number.isNaN(day)) {
            return `${day} ${months[monthIndex]}, ${y}`;
        }
        return trimmed;
    };

    const getItemDisplay = (item) => {
        const name = item.medicationName || 'Medication';
        const hasLotExpiry = (item.lot != null && String(item.lot).trim() !== '') || (item.expiry != null && String(item.expiry).trim() !== '');
        if (hasLotExpiry) {
            const lot = (item.lot != null ? String(item.lot).trim() : '') || '—';
            const rawExpiry = (item.expiry != null ? String(item.expiry).trim() : '') || '';
            const expiryDisplay = (item.formatted_expiry_date != null && String(item.formatted_expiry_date).trim() !== '')
                ? String(item.formatted_expiry_date).trim()
                : rawExpiry ? formatExpiryFromDateStr(rawExpiry) || rawExpiry : '—';
            return { name, lot, expiry: expiryDisplay, undefined: false };
        }
        return { name, lot: null, expiry: null, undefined: true };
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden">
                <div className="px-4 py-3 bg-white border-b border-gray-100 flex flex-row items-center justify-between gap-3">
                    <div className="flex flex-row items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#FFF5D6] text-[#DA8800]" aria-hidden>
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2L2 22h20L12 2z" />
                                <path d="M12 9v4m0 4h.01" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div className="flex flex-col gap-0">
                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Prep check confirmation</h3>
                            {patientName ? (
                                <p className="text-xs font-normal text-gray-500 uppercase tracking-wide">Patient: {patientName}</p>
                            ) : null}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={isSubmitting ? undefined : onClose}
                        disabled={isSubmitting}
                        className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Cancel"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="px-4 py-4 min-h-[280px] max-h-[420px] overflow-y-auto">
                    <table className="w-full border-collapse text-sm">
                        <thead>
                            <tr className="border-b-2 border-gray-200 bg-gray-100">
                                <th className="text-left py-2.5 px-3 font-semibold text-gray-700 uppercase">Medication</th>
                                <th className="text-left py-2.5 px-3 font-semibold text-gray-700 uppercase">Lot</th>
                                <th className="text-left py-2.5 px-3 font-semibold text-gray-700 uppercase whitespace-nowrap">Expiry</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayItems.map((item, idx) => {
                                const { name, expiry, undefined: isUndefined } = getItemDisplay(item);
                                return (
                                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
                                        <td className="py-2 px-3 text-gray-900 font-medium">
                                            {name}
                                            {item.duplicateCount > 1 ? (
                                                <span className="ml-2 text-[11px] font-semibold text-gray-500 normal-case">
                                                    x{item.duplicateCount}
                                                </span>
                                            ) : null}
                                        </td>
                                        <td className="py-2 px-3 text-gray-700">
                                            <input
                                                type="text"
                                                value={item.lot ?? ''}
                                                onChange={(event) => handleGroupChange(item.sourceIndexes, 'lot', event.target.value)}
                                                disabled={isSubmitting}
                                                placeholder="Lot #"
                                                className="w-full min-w-[110px] text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                                            />
                                        </td>
                                        <td className="py-2 px-3 text-gray-700 whitespace-nowrap">
                                            <input
                                                type="text"
                                                id={`prep_confirm_expiry_${idx}`}
                                                value={item.expiry || ''}
                                                readOnly
                                                disabled={isSubmitting}
                                                title={isUndefined ? '' : expiry}
                                                className="w-full min-w-[145px] text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="flex border-t border-gray-200 bg-gray-50/50 rounded-b-xl overflow-hidden">
                    <button
                        type="button"
                        onClick={handleChange}
                        disabled={isSubmitting}
                        className="flex-1 py-3.5 text-sm font-semibold text-gray-600 bg-white hover:bg-gray-100 transition-colors border-r border-gray-200 rounded-bl-xl disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                    >
                        Change
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className="flex-1 py-3.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm rounded-br-xl disabled:cursor-not-allowed disabled:bg-blue-400"
                    >
                        <span className="inline-flex items-center justify-center gap-2">
                            {isSubmitting ? (
                                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                                    <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                </svg>
                            ) : null}
                            {isSubmitting ? 'Processing...' : 'Confirm'}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export const MuteRXModal = ({ setShowMuteRXModal, rxnumber, rxnumbers, onMuteSuccess, formData, selectedPatient, type, prepButNotBilledPageNumber, prepButNotBilledSortType }) => {

    const dispatch = useCustomDispatch()
    const { muteRxResponse } = useAccountibilitySelector()
    const isBulk = Array.isArray(rxnumbers) && rxnumbers.length > 0


    const UpdateMuteRX = () => {
        const status = type === 'investigate' ? selectedPatient?.is_mute === 1 ? false : true : true
        const payload = isBulk
            ? { rx_numbers: rxnumbers, status }
            : { rx_number: rxnumber, status }

        if (isBulk) {
            dispatch(handleMuteRX({
                fetchType: 'muteRX-bulk',
                payload: payload,
                type: type,
                prepButNotBilledPageNumber: prepButNotBilledPageNumber,
                prepButNotBilledSortType: prepButNotBilledSortType,
                setShowMuteRXModal: setShowMuteRXModal,
                onSuccess: onMuteSuccess
            }))
            return
        }

        if (type === 'prepcheck') {
            dispatch(handleMuteRX({
                fetchType: `muteRX-${rxnumber}`,
                payload: payload,
                selectedPatient: selectedPatient,
                formData: formData,
                setShowMuteRXModal: setShowMuteRXModal,
                type: type
            }))
        }

        if (type === 'notbilled') {
            dispatch(handleMuteRX({
                fetchType: `muteRX-${rxnumber}`,
                payload: payload,
                type: type,
                prepButNotBilledPageNumber: prepButNotBilledPageNumber,
                prepButNotBilledSortType: prepButNotBilledSortType,
                setShowMuteRXModal: setShowMuteRXModal
            }))
        }
        if (type === 'donotbilled') {
            dispatch(handleMuteRX({
                fetchType: `muteRX-${rxnumber}`,
                payload: payload,
                type: type,
                prepButNotBilledPageNumber: prepButNotBilledPageNumber,
                prepButNotBilledSortType: prepButNotBilledSortType,
                setShowMuteRXModal: setShowMuteRXModal
            }))
        }
        if (type === 'investigate') {
            dispatch(handleMuteRX({
                fetchType: `muteRX-${rxnumber}`,
                payload: payload,
                type: type,
                setShowMuteRXModal: setShowMuteRXModal
            }))
        }
    }

    return (<>
        <div className="fixed top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center p-6 overflow-x-hidden overflow-y-auto bg-black bg-opacity-50">
            <div className="relative w-full max-w-md max-h-full">
                <div className="relative bg-white rounded-lg shadow p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-2 rounded-full border-2 border-yellow-400 flex items-center justify-center">
                        <span className=" text-lg">⚠️</span>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        {isBulk
                            ? `Are you sure you want to mute ${rxnumbers.length} selected prescription${rxnumbers.length === 1 ? '' : 's'}?`
                            : <>Are you sure you want to {type === 'investigate' ? selectedPatient?.is_mute === 1 ? 'unmute' : 'mute' : 'mute'} this prescription (Rx → {rxnumber})?</>}
                    </h3>

                    {isBulk &&
                        <p className="text-sm text-gray-600 mb-2 max-h-24 overflow-y-auto break-words">
                            Rx → {rxnumbers.join(', ')}
                        </p>}

                    {/* <p>
                        This action cannot be undone.
                    </p> */}

                    <div className="flex justify-center gap-3 mt-2">
                        <button
                            disabled={muteRxResponse?.loading}
                            onClick={UpdateMuteRX}
                            className="px-6 py-2 bg-[#D97706] text-white rounded-md hover:bg-[#D97706] flex gap-2">
                            {muteRxResponse?.loading && <Loader color='white' />}
                            Yes, {type === 'investigate' ? selectedPatient?.is_mute === 1 ? 'unmute' : 'mute' : 'mute'} it
                        </button>

                        <button
                            onClick={() => setShowMuteRXModal(false)}
                            className="px-6 py-2 bg-[#F3F4F6] text-gray-600 rounded-md hover:bg-[#F3F4F6]">
                            No, cancel !
                        </button>

                    </div>
                </div>
            </div>
        </div>
    </>)
}




export const handleBulkDownloadLabel = (dispatch, formData, pickupRoute, value, scope) => {
    const payload = {
        search_date: formData?.date,
        route: pickupRoute,
        next_number_of_days: formData?.next_number_of_days?.value
    }

    if (value) {
        payload.label_print_type = value
    }

    if (scope?.patient_id != null) {
        payload.patient_id = scope.patient_id
    }
    if (scope?.rxnumber != null && String(scope.rxnumber) !== '') {
        payload.rxnumber = scope.rxnumber
    }

    dispatch(DownloadBulkLabel({
        fetchType: `downloadBulkLabel-${value}`,
        payload: payload
    }))
}





export const ClinicName = ({
    formData,
    errorMessages,
    setFormData,
    setErrorMessages,
}) => {
    const { getClinicsData, getAllClinicsResponse } = useCustomPatientSelectors();
    const [clinicList, setClinicList] = useState([]);
    const dispatch = useCustomDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
    const abortController = useRef(null);
    const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);

    useEffect(() => {
        const clinics = getClinicsData || [];
        const formattedClinics = clinics?.map((clinic) => ({
            label: `${clinic.name} (${clinic.fax})`,
            value: clinic.id,
            fax: clinic.fax,
        }));

        setClinicList(formattedClinics);
    }, [getClinicsData]);

    useEffect(() => {
        if (debouncedSearchTerm) {
            dispatch(fetchAllClinics(`${debouncedSearchTerm}`, abortController.current));
        }
    }, [debouncedSearchTerm, dispatch]);

    useEffect(() => {
        if (getAllClinicsResponse?.loading) {
            setErrorMessages({
                ...errorMessages,
                clinicName: "",
            });
        } else if (getAllClinicsResponse?.error !== null) {
            setErrorMessages({
                ...errorMessages,
                clinicName:
                    "Getting an issue while fetching request please try to reload the page or try again",
            });
        } else {
            setErrorMessages({
                ...errorMessages,
                clinicName: "",
            });
        }
    }, [getAllClinicsResponse]);

    const handleFetchCliniList = () => {
        // Only fetch if initial data is not loaded or if there's a search term
        if (!isInitialDataLoaded || searchTerm) {
            abortController.current?.abort();
            abortController.current = new AbortController();
            dispatch(fetchAllClinics(searchTerm, abortController.current));
            setIsInitialDataLoaded(true);
        }
    };

    const handleInputChange = (inputValue) => {
        setSearchTerm(inputValue);
    };

    return (
        <div className={`${!errorMessages.clinicName && clinicList?.length !== 0 && "select-wrapper"}`}>
            <Select
                isMulti={false}
                onChange={(selectedValue) => {
                    setFormData(
                        {
                            ...formData,
                            clinicName: selectedValue,
                            clinicFaxNumber: selectedValue.fax ? selectedValue.fax : '',
                        },
                        setErrorMessages({
                            ...errorMessages,
                            clinicName: "",
                            clinicFaxNumber: "",
                        })
                    );
                }}
                onInputChange={handleInputChange}
                onMenuOpen={handleFetchCliniList}
                value={formData?.clinicName}
                options={clinicList}
                classNamePrefix="react-select"
                menuPosition="auto"
                styles={{
                    control: (provided) => ({
                        ...provided,
                        height: 38,
                        paddingLeft: "4.8px",
                        color: "#6B7280",
                        borderRadius: "4px",
                        position: "relative",
                        top: "0px",
                        border: errorMessages.clinicName
                            ? "1px solid red"
                            : "1px solid lightgrey",
                    }),
                    menuList: (provided) => ({
                        ...provided,
                        maxHeight: "200px",
                        overflowY: "auto",
                    }),
                    menu: (provided, state) => ({
                        ...provided,
                        marginTop: state.placement === 'top' ? "0px" : "2px",
                        marginBottom: state.placement === 'top' ? "2px" : "0px",
                        maxHeight: "200px",
                        overflowY: "visible",
                        zIndex: 99999
                    }),
                }}
                isSearchable={true}
                noOptionsMessage={() =>
                    getAllClinicsResponse?.loading ? "Loading..." : "No Options"
                }
                className={`basic-multi-select`}
                placeholder={`${errorMessages.clinicName ? "" : "Select Clinic"}`}
            />


        </div>
    );
};




export const DoctorList = ({
    formData,
    errorMessages,
    setFormData,
    setErrorMessages,
    placeholder = "Select Doctor"
}) => {
    const { doctorList, doctorsListResponse } = useCustomPatientSelectors();
    const [doctorLists, setDoctorLists] = useState([]);
    const dispatch = useCustomDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
    const abortController = useRef(null);
    const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);

    useEffect(() => {
        const list = doctorList || [];
        const formattedClinics = list?.map((value) => ({
            label: `${value.last_name} ${value.first_name} ${value.cps_id}`,
            value: value.id,
            cps_id: value.cps_id,
        }));

        setDoctorLists(formattedClinics);
    }, [doctorList]);

    const handleFetchDoctorList = () => {
        // Only fetch if initial data is not loaded or if there's a search term
        if (!isInitialDataLoaded || searchTerm) {
            abortController.current?.abort();
            abortController.current = new AbortController();
            dispatch(FetchAllDoctorsList(searchTerm, abortController.current));
            setIsInitialDataLoaded(true);
        }
    };

    useEffect(() => {
        if (debouncedSearchTerm) {
            dispatch(FetchAllDoctorsList(`${debouncedSearchTerm}`, abortController.current));
        }
    }, [debouncedSearchTerm, dispatch]);

    useEffect(() => {
        if (doctorsListResponse?.loading) {
            setErrorMessages({
                ...errorMessages,
                doctorName: "",
            });
        } else if (doctorsListResponse?.error !== null) {
            setErrorMessages({
                ...errorMessages,
                doctorName:
                    "Getting an issue while fetching request please try to reload the page or try again",
            });
        } else {
            setErrorMessages({
                ...errorMessages,
                doctorName: "",
            });
        }
    }, [doctorsListResponse]);

    const handleInputChange = (inputValue) => {
        setSearchTerm(inputValue);
    };

    return (
        <div>
            <Select
                isMulti={false}
                onInputChange={handleInputChange}
                onMenuOpen={handleFetchDoctorList}
                onChange={(selectedValue) => {
                    setFormData(
                        {
                            ...formData,
                            doctorName: selectedValue,
                            prescriptionCPID: selectedValue.cps_id,
                        },
                        setErrorMessages({ ...errorMessages, doctorName: "", prescriptionCPID: '' })
                    );
                }}
                value={formData?.doctorName}
                options={doctorLists}
                classNamePrefix="react-select"
                menuPosition="auto"
                isSearchable={true}
                noOptionsMessage={() =>
                    doctorsListResponse?.loading ? "Loading..." : "No Options"
                }
                className="basic-multi-select"
                placeholder={`${errorMessages.doctorName ? "" : placeholder}`}
                styles={{
                    control: (provided) => ({
                        ...provided,
                        height: 38,
                        paddingLeft: "4.8px",
                        color: "#6B7280",
                        borderRadius: "4px",
                        position: "relative",
                        top: "0px",
                        border: errorMessages.doctorName
                            ? "1px solid red"
                            : "1px solid lightgrey",
                    }),
                    menuList: (provided) => ({
                        ...provided,
                        maxHeight: "200px",
                        overflowY: "auto",
                    }),
                    menu: (provided, state) => ({
                        ...provided,
                        marginTop: state.placement === 'top' ? "0px" : "2px",
                        marginBottom: state.placement === 'top' ? "2px" : "0px",
                        maxHeight: "200px",
                        overflowY: "visible",
                        zIndex: 9999
                    }),
                }}
            />

        </div>
    );
};





export const ClinicFaxNumber = ({
    handleFormDataChange,
    formData,
    errorMessages,
    className = '',
    labelClassName = ''
}) => {
    // Function to format fax number as (XXX) XXX-XXXX
    const formatFaxNumber = (value) => {
        // Remove all non-digits
        const numbers = value.replace(/\D/g, '');

        // Format based on length
        if (numbers.length <= 3) {
            return numbers;
        } else if (numbers.length <= 6) {
            return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
        } else {
            return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
        }
    };

    // Custom onChange handler for formatting
    const handleFaxNumberChange = (e) => {
        const formattedValue = formatFaxNumber(e.target.value);

        // Create a synthetic event with the formatted value
        const syntheticEvent = {
            ...e,
            target: {
                ...e.target,
                name: 'clinicFaxNumber',
                value: formattedValue
            }
        };

        handleFormDataChange(syntheticEvent);
    };

    return (
        <div className="relative ">
            <input
                autoComplete="off"
                type="text"
                id="clinicFaxNumber"
                onChange={handleFaxNumberChange}
                name="clinicFaxNumber"
                value={formData.clinicFaxNumber}
                className={`block pl-4 px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer cursor-text bg-white ${errorMessages.clinicFaxNumber && "border-red-600"
                    } ${className}`}
                placeholder=""
                maxLength={14}
            />
            <label
                htmlFor="clinicFaxNumber"
                className={`ml-4 absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-8 scale-75 top-6  origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-[75%] peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${labelClassName}`}
            >
                {!errorMessages.clinicFaxNumber && "Fax Number"}
            </label>
            {errorMessages.clinicFaxNumber && (
                <p className="text-xs text-red-600 dark:text-red-400">
                    {errorMessages.clinicFaxNumber}
                </p>
            )}
        </div>
    );
};




export const FaxModal = ({
    faxPopup,
    setFaxPopup,
    FaxIcon,
    ModalCloseIcon,
    faxNumber,
    setFaxNumber,
    faxMessageContent,
    setFaxMessageContent,
    handleFaxCancel,
    handleFaxConfirm,
    fetchDilaudidFaxToDoctorResponse
}) => {
    return (
        <>
            {faxPopup.show && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-auto max-h-[95vh] overflow-hidden">
                        {/* Header */}
                        <div className="bg-gray-100 text-gray-800 px-6 py-3 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <FaxIcon className="w-5 h-5 " />
                                    <h3 className="text-lg font-semibold">Fax Preview</h3>
                                </div>
                                <button
                                    onClick={handleFaxCancel}
                                >
                                    <ModalCloseIcon className="w-3 h-3" />
                                </button>
                            </div>
                        </div>


                        <div className="p-4 overflow-y-auto max-h-[calc(95vh-140px)]">
                            {(() => {
                                const { doctorInfo } = faxPopup;
                                const { fax_preview_details: details, drug_name, rxnumber, sig, rx_din } = doctorInfo;

                                return (
                                    <div className="space-y-3">

                                        <div className="border border-gray-200 rounded-md p-2">
                                            <h4 className="font-semibold">
                                                Dr. {details.doctor_name}
                                            </h4>
                                            <p className="text-sm ">{details.address}</p>
                                            <p className="text-sm ">Phone: {details.phone.replace(/\D/g, '')}</p>
                                        </div>

                                        <div className='bg-gray-50 border border-red-200 rounded-md p-3'>
                                            <div className="space-y-3">
                                                {/* Fax Number Input */}
                                                <div className="flex items-center gap-2">
                                                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Fax Number:</label>
                                                    <input
                                                        type="text"
                                                        value={faxNumber || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            const digitsOnly = value.replace(/\D/g, '');
                                                            if (digitsOnly.length <= 10) {
                                                                setFaxNumber(digitsOnly);
                                                            }
                                                        }}
                                                        className="bg-white border border-gray-300 focus:border focus:border-gray-300 rounded px-2 py-1 text-sm w-32 focus:ring-0 focus:ring-blue-500 "
                                                        placeholder="Enter fax number"
                                                        maxLength={10}
                                                    />
                                                </div>

                                                {/* <textarea
                                                value={faxMessageContent}
                                                onChange={(e) => setFaxMessageContent(e.target.value)}
                                                className="w-full h-32 uppercase p-2 text-sm border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                                placeholder="Enter your fax message here..."
                                            /> */}

                                                {/* <CustomTextEditor  placeholder='Description' content={content} setContent={setContent} error={errorMessage.description ? true : false}  /> */}
                                                <RecommendationLetterEditor content={faxMessageContent}
                                                    setContent={setFaxMessageContent} placeholder='Description'
                                                />
                                            </div>
                                        </div>


                                        <div className="border border-gray-200 rounded-md overflow-hidden">
                                            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                                                <h4 className="font-semibold  text-sm">Prescription Details</h4>
                                            </div>
                                            <div className="p-3">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                                    <div className="flex gap-1">
                                                        <span className="font-medium ">Drug:</span>
                                                        <span >{rx_din} {drug_name}</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <span className="font-medium ">Orig Date:</span>
                                                        <span >{details.orig_date}</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <span className="font-medium ">Prev Date:</span>
                                                        <span >{details.prev_date}</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <span className="font-medium ">Doctor:</span>
                                                        <span >{details.doctor_name} {details.rxdea}/{details.rxcollege}</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <span className="font-medium ">Qty:</span>
                                                        <span >{details.qty}</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <span className="font-medium ">Fills authorized:</span>
                                                        <span >{details.fill_authorized}</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <span className="font-medium ">Qty Filled:</span>
                                                        <span >{details.qty_fills}</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <span className="font-medium ">Qty Remaining:</span>
                                                        <span >{details.qty_remaining}</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <span className="font-medium ">Rx #:</span>
                                                        <span >{rxnumber}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>


                                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                                            <p className=" text-sm">
                                                <span className="font-medium">Sig:</span> {sig}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>


                        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-end gap-3">
                            <button
                                onClick={handleFaxCancel}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors font-medium text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleFaxConfirm(faxPopup?.doctorInfo?.rxes_id, faxPopup?.doctorInfo?.rxnumber, faxNumber, faxMessageContent)}
                                disabled={fetchDilaudidFaxToDoctorResponse?.loading && fetchDilaudidFaxToDoctorResponse?.fetchType === `fetchDilaudidFaxToDoctor`}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {fetchDilaudidFaxToDoctorResponse?.loading && fetchDilaudidFaxToDoctorResponse?.fetchType === `fetchDilaudidFaxToDoctor` && <Loader color='white' />}
                                <span>Send Fax</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}



export const checkDeliveryStatus = (processing_dose, total_dose) => {
    // Check if any time zone has "delivered" status
    const hasDelivered = Object.values(processing_dose || {}).some(dose => dose.status === 'delivered');

    if (!hasDelivered) {
        return { status: 'notdeliver', total_dose: total_dose };
    }

    // If there are delivered doses, calculate total dose from all delivered time zones
    const totalDose = Object.values(processing_dose || {}).reduce((total, dose) => {
        if (dose.status === 'delivered') {
            return total + (dose.dose || 0);
        }
        return total;
    }, 0);

    return {
        status: 'delivered',
        total_dose: total_dose,
        total_dose_delivered: totalDose
    };
};



export const checkProcessingDoseStatus = (processing_dose, pacmed_overwrite) => {
    const timeOrder = ['0800', '1200', '1800', '2100', '2200', '2300', '2350'];
    for (const time of timeOrder) {
        const doseInfo = processing_dose?.[time];

        if (!doseInfo) continue;

        const status = doseInfo.status;

        if (status === 'pending') {
            return true;
        }

        if (status === 'delivered') {
            continue;
        }

        if (status === 'missed' || status === 'returned') {
            const pacmedValue = Number(pacmed_overwrite?.[time] || 0);
            if (pacmedValue > 0) {
                return true;
            }
        }
        break;
    }
    return false;
};



// Custom SingleValue component Prep Check labels Dropdown
export const CustomSingleValue = ({ isLoading, ...props }) => (
    <components.SingleValue {...props}>
        <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: 2, // space between icon and text
            height: '100%',
            fontWeight: 500
        }}>
            {isLoading ? <Loader color="white" width={16} height={16} /> : <PrintIcons />}
            <span style={{ lineHeight: 1 }}>{props.children}</span>
        </span>
    </components.SingleValue>
);

export const CustomPlaceholder = ({ isLoading, ...props }) => (
    <components.Placeholder {...props}>
        <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: 2, // space between icon and text
            height: '100%',
            fontWeight: 500
        }}>
            {isLoading ? <Loader color="white" width={16} height={16} /> : <PrintIcons />}
            <span style={{ lineHeight: 1 }}>{props.children}</span>
        </span>
    </components.Placeholder>
);
